import { ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  TrendingUp,
  BarChart3,
  Radar,
  Layers,
  Newspaper,
  Bot,
  Menu,
  X,
  Eye,
  Users,
  ShieldCheck,
  Megaphone,
  UserCog,
} from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { GlobalClock } from "@/components/GlobalClock";
import { TopBar } from "@/components/TopBar";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const { role } = useUserRole();

  const isAccountCenterActive = location.pathname.startsWith("/account");

  const traderNavItems = [
    { icon: LayoutDashboard, label: t.nav.dashboard, path: "/" },
    { icon: Layers, label: isRTL ? "چارچوب معاملاتی" : "Trading Framework", path: "/training" },
    { icon: Bot, label: isRTL ? "دستیار معاملاتی" : "Trade Assistant", path: "/assistant" },
    { icon: TrendingUp, label: isRTL ? "ژورنال معاملات" : "Trade Journal", path: "/trades" },
    { icon: BarChart3, label: isRTL ? "تحلیل ژورنال" : "Journal Analytics", path: "/analytics" },
    { icon: Radar, label: isRTL ? "هوش بازار" : "Market Intelligence", path: "/market-intelligence" },
    { icon: Newspaper, label: isRTL ? "تحلیل بنیادی" : "Fundamental Analysis", path: "/fundamentals" },
  ];

  const portalNavItems = [
    ...(role === "investor" || role === "admin"
      ? [{ icon: Eye, label: isRTL ? "پنل سرمایه‌گذار" : "Investor Panel", path: "/investor" }]
      : []),
    ...(role === "ib" || role === "admin"
      ? [{ icon: Users, label: isRTL ? "پنل IB/همکار" : "IB/Affiliate Panel", path: "/ib" }]
      : []),
    ...(role === "admin"
      ? [
          { icon: ShieldCheck, label: isRTL ? "پنل مدیریت" : "Admin Panel", path: "/admin" },
          { icon: Megaphone, label: isRTL ? "پنل مارکتینگ" : "Marketing Panel", path: "/marketing" },
        ]
      : []),
  ];

  const isPortalOnlyRole = role === "investor" || role === "ib";
  const baseNavItems = isPortalOnlyRole ? [] : traderNavItems;
  const roleNavItems = portalNavItems;
  const showAccountCenterLink = role !== "ib";
  const versionText = isRTL ? "نسخه 1.0.0" : "Version 1.0.0";

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b shadow-sm">
        <div className="flex items-center justify-between p-4">
          <h1 className="text-xl font-bold">
            {isRTL ? "ژورنال معاملاتی" : "Trading Journal"}
          </h1>
          <div className="flex items-center gap-2">
            <GlobalClock />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Top Bar */}
      <TopBar isRTL={isRTL} />

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-16 z-40 h-[calc(100vh-4rem)] w-64 bg-card border-l transition-transform duration-300",
          isRTL ? "right-0 border-l" : "left-0 border-r",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : isRTL ? "translate-x-full" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Account Center box */}
            {showAccountCenterLink && (
              <Link
                to="/account/overview"
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors border mb-2",
                  isAccountCenterActive
                    ? "bg-primary text-primary-foreground font-medium border-primary"
                    : "text-foreground hover:bg-muted border-border bg-muted/30"
                )}
              >
                <UserCog className="w-5 h-5" />
                <span>{isRTL ? "مرکز حساب" : "Account Center"}</span>
              </Link>
            )}

            {baseNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {roleNavItems.length > 0 && (
              <>
                <div className="my-2 border-t border-border/50" />
                {roleNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-foreground hover:bg-muted"
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          <div className="p-4 border-t">
            <p className="text-xs text-muted-foreground text-center">
              {versionText}
            </p>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={cn("min-h-screen pt-16 lg:pt-16", isRTL ? "lg:mr-64" : "lg:ml-64")}>
        <PaymentTestModeBanner />
        {children}
      </main>
    </div>
  );
};
